import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  return localStorage.getItem("token") || "";
}

// Simple notification sound using Web Audio API
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = "sine";
    gain.gain.value = 0.15;
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  } catch {}
}

export function useChatNotifications() {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const permissionRef = useRef("Notification" in window ? Notification.permission : "denied");

  // Request notification permission on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then(p => { permissionRef.current = p; });
    }
  }, []);

  const showNotification = useCallback((title, body, tag) => {
    // Only notify if tab is not focused
    if (document.visibilityState === "visible" && window.location.pathname.includes("/chat")) return;

    playNotificationSound();

    if ("Notification" in window && Notification.permission === "granted") {
      const n = new Notification(title, {
        body: body.slice(0, 100),
        tag,
        icon: "/favicon.ico",
        silent: true, // We play our own sound
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
      setTimeout(() => n.close(), 5000);
    }
  }, []);

  useEffect(() => {
    const token = getToken();
    if (!token || !user) return;

    const wsUrl = API_URL.replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/api/ws/chat?token=${token}`);
    wsRef.current = ws;

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === "channel_message" && data.message?.sender_id !== user.id) {
          const content = data.message.content || (data.message.file_name ? `Shared a file: ${data.message.file_name}` : "");
          showNotification(
            `#${data.channel_name || "Channel"}`,
            `${data.message.sender_name}: ${content}`,
            `ch-${data.message.channel_id}`
          );
        } else if (data.type === "dm_message" && data.message?.sender_id !== user.id) {
          const content = data.message.content || (data.message.file_name ? `Shared a file: ${data.message.file_name}` : "");
          showNotification(
            `Message from ${data.message.sender_name}`,
            content,
            `dm-${data.thread_id}`
          );
        } else if (data.type === "call_request") {
          showNotification(
            "Incoming Video Call",
            `${data.from_user_name} is calling you...`,
            `call-${data.from_user_id}`
          );
        }
      } catch {}
    };

    // Keep alive
    const ping = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send("ping");
    }, 25000);

    ws.onclose = () => {};

    return () => {
      clearInterval(ping);
      ws.close();
    };
  }, [user, showNotification]);
}
